import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { LOCAL_STORAGE_KEYS } from "app/core/constants/local-storage-keys";
import { Observable, of } from "rxjs";
import { map, share, tap } from "rxjs/operators";
import { JWT_CLAIMS } from "../constants/claim-types";
import { ChangePassword } from "../models/auth/change-password";
import { LoginResponse } from "../models/auth/login-response";
import { Role } from "../models/auth/role";
import { User, PagedResult, UsersQueryParams } from "../models/auth/user";
import { ForgotPassword } from "../models/forgot-password";
import { ResetPassword } from "../models/reset-password";
import { LoadUserAction, SetUserAction } from "../store/actions/auth.actions";
import { AppState } from "../store/reducers/app.reducer";
import { selectUser } from "../store/selectors";
import { AppHttpClient } from "./http.client";
import { LocalStorageService } from "./local-storage.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private refreshTokenObservable?: Observable<LoginResponse>;

  constructor(
    private router: Router,
    private http: AppHttpClient,
    private localStorageService: LocalStorageService,
    private store: Store<AppState>
  ) {}

  getUsername(): Observable<string> {
    return this.store.select(selectUser).pipe(map((x) => x && x.username));
  }

  getRole(): Observable<string> {
    let role = "";

    if (this.isLoggedIn) {
      role = this.claims[JWT_CLAIMS.Role];
    }

    return of(role);
  }

  createUser(user: User): Observable<User> {
    return this.http.post<User>(`auth/create-user`, user);
  }

  updateUser(user: User): Observable<User> {
    return this.http.post<User>(`auth/update-user`, user);
  }

  disableUser(user: User): Observable<User> {
    return this.http.post<User>(`auth/disable-user`, user);
  }

  enableUser(user: User): Observable<User> {
    return this.http.post<User>(`auth/enable-user`, user);
  }

  getUsers(): Observable<PagedResult<User>> {
    // Returns all users when no pagination params are provided
    // Response format: { value: User[], totalCount: number, pageSize: number }
    return this.http.get<PagedResult<User>>("auth/users");
  }

  getUsersPaginated(
    params: UsersQueryParams = {}
  ): Observable<PagedResult<User>> {
    let httpParams = new HttpParams();

    console.log("=== getUsersPaginated ===");
    console.log("Input params:", params);

    if (params.limit !== undefined) {
      httpParams = httpParams.set("limit", params.limit.toString());
    }
    if (params.skip !== undefined) {
      httpParams = httpParams.set("skip", params.skip.toString());
    }
    if (params.search) {
      console.log("Adding search param:", params.search);
      httpParams = httpParams.set("search", params.search);
    }
    if (params.projectId) {
      httpParams = httpParams.set("projectId", params.projectId);
    }
    if (params.isInternal !== undefined) {
      httpParams = httpParams.set("isInternal", params.isInternal.toString());
    }
    if (params.sortBy) {
      httpParams = httpParams.set("sortBy", params.sortBy);
    }

    console.log("Final HttpParams:", httpParams.toString());
    console.log("HttpParams keys:", httpParams.keys());

    return this.http.get<PagedResult<User>>("auth/users", {
      params: httpParams,
    });
  }

  getActiveUsers(): Observable<User[]> {
    return this.http.get<User[]>("auth/active-users");
  }

  getUserById(id: string): Observable<User> {
    this.store.dispatch(new LoadUserAction(true));
    return this.http.get<User>(`auth/users/${id}`).pipe(
      tap((user) => {
        this.store.dispatch(new LoadUserAction(false));
        return user;
      })
    );
  }

  login(username: string, password: string) {
    return this.http
      .post<LoginResponse>("auth/login", {
        username,
        password,
      })
      .pipe(
        map((loginResponse) => {
          if (loginResponse) {
            this.setAuthToken(loginResponse);
            this.store.dispatch(new SetUserAction(loginResponse.user));
            this.startRefreshTokenTimer();
          }
          return loginResponse;
        })
      );
  }

  ssoLogin() {
    return this.http.post<LoginResponse>("auth/sso-login", {}).pipe(
      map((loginResponse) => {
        if (loginResponse) {
          this.setAuthToken(loginResponse);
          this.store.dispatch(new SetUserAction(loginResponse.user));
          this.startRefreshTokenTimer();
        }
        return loginResponse;
      })
    );
  }

  changePassword(changePassword: ChangePassword): Observable<boolean> {
    return this.http.post<boolean>(`auth/change-password`, changePassword);
  }

  logout() {
    this.http.post<any>("auth/revoke", {}).subscribe();
    this.removeAuthToken();
    this.stopRefreshTokenTimer();
    this.router.navigate(["auth/logged-out"]);
  }

  refreshAuthToken() {
    if (!this.refreshTokenObservable) {
      this.refreshTokenObservable = this.http
        .post<LoginResponse>("auth/refresh", {
          token: this.token,
          refreshToken: this.refreshToken,
        })
        .pipe(
          share(),
          map((loginResponse) => {
            if (!loginResponse) {
              this.logout();
              return;
            }
            this.setAuthToken(loginResponse);
            this.startRefreshTokenTimer();
            return loginResponse;
          })
        );
    }

    return this.refreshTokenObservable;
  }

  restoreAuthState() {
    if (this.token) {
      if (this.tokenExpired) {
        this.refreshAuthToken().subscribe((loginResponse) => {
          this.store.dispatch(new SetUserAction(loginResponse.user));
          this.startRefreshTokenTimer();
        });
      } else {
        let userId = this.claims[JWT_CLAIMS.Id];

        this.getUserById(userId).subscribe((user) => {
          this.store.dispatch(new SetUserAction(user));
        });

        this.startRefreshTokenTimer();
      }
    }
  }

  get isLoggedIn() {
    return !!this.token;
  }

  get claims() {
    if (this.isLoggedIn) {
      return JSON.parse(atob(this.token.split(".")[1]));
    }
  }

  get tokenExpired(): boolean {
    let expiry = this.claims[JWT_CLAIMS.Expiry];
    let now = Math.floor(Date.now() / 1000);
    return expiry <= now;
  }

  public get token(): string {
    return this.localStorageService.get(LOCAL_STORAGE_KEYS.AuthToken);
  }

  public get isSSO(): boolean {
    if (this.isLoggedIn) return this.claims[JWT_CLAIMS.IsSSO];
  }

  public get refreshToken() {
    return this.localStorageService.get(LOCAL_STORAGE_KEYS.RefreshAuthToken);
  }

  forgotPassword(forgotPassword: ForgotPassword): Observable<boolean> {
    return this.http.post<boolean>(`auth/forgot-password`, forgotPassword);
  }

  isValidResetPasswordToken(token: string): Observable<boolean> {
    return this.http.post<boolean>(
      `auth/validate-reset-password-token/${token}`,
      null
    );
  }

  resetPassword(resetPassword: ResetPassword): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`auth/reset-password`, resetPassword)
      .pipe(tap((response) => this.setAuthToken(response)));
  }

  getRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`auth/roles`);
  }

  clearCache(): Observable<any> {
    return this.http.get<any>(`submissions/clear-all-cache`);
  }
  private setToken(token) {
    this.localStorageService.set(LOCAL_STORAGE_KEYS.AuthToken, token);
  }

  private setRefreshToken(refreshToken) {
    this.localStorageService.set(
      LOCAL_STORAGE_KEYS.RefreshAuthToken,
      refreshToken
    );
  }

  private setAuthToken(loginResponse: LoginResponse): LoginResponse {
    if (loginResponse && loginResponse.token) {
      this.setToken(loginResponse.token);
      this.setRefreshToken(loginResponse.refreshToken);
    }

    return loginResponse;
  }

  private removeAuthToken() {
    this.localStorageService.remove(LOCAL_STORAGE_KEYS.AuthToken);
    this.localStorageService.remove(LOCAL_STORAGE_KEYS.RefreshAuthToken);
  }

  private refreshTokenTimeout;

  private startRefreshTokenTimer() {
    const jwtToken = JSON.parse(atob(this.token.split(".")[1]));

    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshAuthToken().subscribe(),
      timeout
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
